import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import {
  getHomeRoleSwitcherOptions,
  readActiveHomeRole,
  writeActiveHomeRole,
} from "../utils/homeRole";
import { homeRoleLabel } from "./HomeRolePicker";
import PlayerRoleIcon from "./PlayerRoleIcon";
import CoachRoleIcon from "./CoachRoleIcon";

function RoleGlyph({ role, fontSize = 28 }) {
  const sx = { fontSize, color: "currentColor" };
  if (role === "coach" || role === "monitor") return <CoachRoleIcon sx={sx} />;
  if (role === "admin" || role === "committee" || role === "moderator") {
    return <AdminPanelSettingsIcon sx={sx} />;
  }
  if (role === "parent") return <FamilyRestroomIcon sx={sx} />;
  return <PlayerRoleIcon sx={sx} />;
}

/**
 * Home header role mark — same footprint as the club logo.
 * Multi-role accounts open a switcher on tap.
 */
export function HeaderRoleBadge({ user, viewAsRole, className = "" }) {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: null, right: 0 });
  const roles = useMemo(
    () => getHomeRoleSwitcherOptions(user),
    [
      user?.user_id,
      user?.type_user_id,
      user?.all_roles,
      user?.hasCoachRole,
      user?.hasAdminRole,
      user?.hasParentRole,
      user?.hasCommitteeRole,
      user?.hasMonitorRole,
      user?.hasPlayerRole,
      user?.hasModeratorRole,
    ]
  );
  const [role, setRole] = useState(
    () => viewAsRole || readActiveHomeRole(user)
  );

  useEffect(() => {
    setRole(viewAsRole || readActiveHomeRole(user));
  }, [user?.user_id, viewAsRole]);

  useEffect(() => {
    const sync = () => setRole(viewAsRole || readActiveHomeRole(user));
    window.addEventListener("smarttt-home-role-changed", sync);
    return () => window.removeEventListener("smarttt-home-role-changed", sync);
  }, [user?.user_id, viewAsRole]);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const el = btnRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const menuWidth = 200;
      const gap = 8;
      const top = Math.round(rect.bottom + gap);
      // Prefer right-align under the button (home). Use left when that would clip.
      const rightAlignedLeft = rect.right - menuWidth;
      if (rightAlignedLeft < gap) {
        setMenuPos({
          top,
          left: Math.round(Math.max(gap, rect.left)),
          right: null,
        });
      } else {
        setMenuPos({
          top,
          left: null,
          right: Math.round(Math.max(gap, window.innerWidth - rect.right)),
        });
      }
    };
    place();
    const onDoc = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        const menu = document.getElementById("header-role-menu-portal");
        if (menu && menu.contains(event.target)) return;
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!roles.length) return null;

  const current = roles.includes(role) ? role : roles[0] || "player";
  const canSwitch = roles.length > 1;
  const label = homeRoleLabel(t, current, user);

  const onPick = (next) => {
    if (!next || next === current) {
      setOpen(false);
      return;
    }
    setRole(next);
    writeActiveHomeRole(next);
    setOpen(false);
  };

  const menu =
    canSwitch && open
      ? createPortal(
          <div
            id="header-role-menu-portal"
            className="header-role-menu header-role-menu--portal"
            role="menu"
            aria-label={t("profileSwitchRole")}
            style={{
              top: menuPos.top,
              ...(menuPos.left != null
                ? { left: menuPos.left, right: "auto" }
                : { right: menuPos.right, left: "auto" }),
            }}
          >
            <p className="header-role-menu__legend">{t("profileSwitchRole")}</p>
            {roles.map((id) => {
              const selected = id === current;
              return (
                <button
                  key={id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  className={`header-role-menu__item${selected ? " is-selected" : ""}`}
                  onClick={() => onPick(id)}
                >
                  <span className="header-role-menu__icon" aria-hidden="true">
                    <RoleGlyph role={id} fontSize={22} />
                  </span>
                  <span className="header-role-menu__label">{homeRoleLabel(t, id, user)}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )
      : null;

  return (
    <div
      className={`header-role-badge ${className}`.trim()}
      ref={rootRef}
    >
      <button
        ref={btnRef}
        type="button"
        className={`header-role-btn header-role-btn--${current}${
          canSwitch ? " is-switchable" : ""
        }${open ? " is-open" : ""}`}
        onClick={() => {
          if (canSwitch) setOpen((v) => !v);
        }}
        aria-haspopup={canSwitch ? "menu" : undefined}
        aria-expanded={canSwitch ? open : undefined}
        aria-label={
          canSwitch
            ? `${label}. ${t("profileSwitchRole")}`
            : label
        }
        title={canSwitch ? t("profileSwitchRole") : label}
        disabled={!canSwitch}
      >
        <RoleGlyph role={current} />
      </button>
      {menu}
    </div>
  );
}
